import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Code2, Upload, History, List, Search, Wallet } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SingleEventTester } from '../components/SingleEventTester'
import { BulkUploadTester } from '../components/BulkUploadTester'
import { RequestHistory } from '../components/RequestHistory'
import { EventsBrowser } from '../components/EventsBrowser'
import { CustomerLookup } from '../components/CustomerLookup'
import { BalanceInspector } from '../components/BalanceInspector'
import type { EventMapping } from '@/features/derivation-config/types'

type TabType = 'single-event' | 'events-browser' | 'customer-lookup' | 'bulk-upload' | 'history' | 'balance-inspector'

interface Tab {
  id: TabType
  label: string
  icon: React.ReactNode
}

const tabs: Tab[] = [
  {
    id: 'single-event',
    label: 'Single Event API',
    icon: <Code2 className="h-4 w-4" />,
  },
  {
    id: 'events-browser',
    label: 'Events Browser',
    icon: <List className="h-4 w-4" />,
  },
  {
    id: 'customer-lookup',
    label: 'Customer Lookup',
    icon: <Search className="h-4 w-4" />,
  },
  {
    id: 'bulk-upload',
    label: 'Bulk Upload',
    icon: <Upload className="h-4 w-4" />,
  },
  {
    id: 'history',
    label: 'Request History',
    icon: <History className="h-4 w-4" />,
  },
  {
    id: 'balance-inspector',
    label: 'Balance Inspector',
    icon: <Wallet className="h-4 w-4" />,
  },
]

export function DeveloperConsolePage() {
  const location = useLocation()
  const locationState = location.state as { mapping?: EventMapping; tab?: TabType } | null
  const [activeTab, setActiveTab] = useState<TabType>(locationState?.tab ?? 'single-event')

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Developer Console</h1>
        <p className="text-gray-500 mt-1">Test and validate Ingestion API endpoints</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {activeTab === 'single-event' && <SingleEventTester />}

        {activeTab === 'events-browser' && <EventsBrowser initialMapping={locationState?.mapping} />}

        {activeTab === 'customer-lookup' && <CustomerLookup />}

        {activeTab === 'bulk-upload' && <BulkUploadTester />}

        {activeTab === 'history' && <RequestHistory />}

        {activeTab === 'balance-inspector' && <BalanceInspector />}
      </div>
    </div>
  )
}
